import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, filter } from 'rxjs';
import { SafeHtml } from '@angular/platform-browser';
import { detectVimeo, getVideoThumbnail } from '../../../utilities/video.utils';
import { modifiedSetTimeout } from '../../../utilities/ZoneUtils';
import {
  EmbedVimeoVideoPipe,
  VIMEO_EMBEDDED_CONTENT_STATUS,
} from '../../../pipes/embed-vimeo-video.pipe';
import { VimeoUploadService } from '../../../services/vimeo-upload.service';
import { EmbedVideoService } from '../../../services/embed-video.service';
import { VideoConfigurations } from '../items-canvas/board-items/board-video-configurations';
import { ResourceItemModel } from '../../../models/resource';
import { UserService } from '../../../services/user.service';
import { AppIconSource } from '../../../services/items-canvas.service';
import { LoadingLayoutType } from '../../../ui/loading/loading.component';

@UntilDestroy()
@Component({
  selector: 'app-video',
  templateUrl: './video.component.html',
  styleUrls: ['./video.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VideoComponent implements OnInit, AfterViewInit, OnChanges {
  @Input() state!: VideoConfigurations;
  @Input() static = false;

  @Output() playModeChanged = new EventEmitter<boolean>();
  @Output() stateChanged = new EventEmitter<VideoConfigurations>();
  @Output() videoReady = new EventEmitter<void>();

  readonly videoThumbnail$ = new BehaviorSubject<string | undefined>(undefined);
  readonly displayVideoTooltip$ = new BehaviorSubject<boolean>(false);
  readonly resettingVideo = new BehaviorSubject<boolean>(false);
  readonly playModeEnabled$ = new BehaviorSubject<boolean>(false);
  readonly showUploadIndicator$ = new BehaviorSubject(false);

  // can't be made an observable because it needs to be added using property binding due to security reasons
  videoIframe?: SafeHtml;

  constructor(
    private embedService: EmbedVideoService,
    private vimeoService: VimeoUploadService,
    private cd: ChangeDetectorRef,
    private userService: UserService,
  ) {
    this.playModeEnabled$
      .pipe(untilDestroyed(this))
      .subscribe((enabled) => this.playModeChanged.emit(enabled));
  }

  ngOnInit(): void {
    this.generateVideoIframe();
  }

  ngAfterViewInit(): void {
    this.showUploadIndicator$.next(this.state.ownerId === this.userService.userId);
  }

  ngOnChanges() {
    this.generateVideoIframe();
  }

  async generateVideoIframe() {
    if (!this.state.url || this.static || this.videoIframe) {
      return;
    }
    const options = {
      attr: { width: 600, height: 400 },
    };
    const url = new URL(this.state.url);

    // Vimeo videos can be uploaded by us or can already exists on Vimeo
    // if video is uploaded by us, we should poll the video status because it might not be ready yet for embedding
    if (detectVimeo(url)) {
      new EmbedVimeoVideoPipe(this.embedService, this.vimeoService)
        .transform(this.state.url, options)
        .pipe(
          untilDestroyed(this),
          filter((embedData) => embedData.status === VIMEO_EMBEDDED_CONTENT_STATUS.READY),
        )
        .subscribe((embedData) => this.setVideoIframe(embedData.innerHTML));
    } else {
      this.setVideoIframe(this.embedService.embed(this.state.url, options));
    }
  }

  setVideoIframe(videoIframe: SafeHtml | undefined) {
    this.videoIframe = videoIframe;
    this.cd.detectChanges();
    if (this.state.url) {
      this.videoThumbnail$.next(getVideoThumbnail(this.state.url));
    }
    this.videoReady.emit();
  }

  resetVideo() {
    this.resettingVideo.next(true);
    requestAnimationFrame(() => this.resettingVideo.next(false));
  }

  showTooltip() {
    this.displayVideoTooltip$.next(true);
    modifiedSetTimeout(() => this.displayVideoTooltip$.next(false), 8000);
  }

  disablePlayMode() {
    this.resetVideo();
    this.playModeEnabled$.next(false);
  }

  enablePlayMode() {
    this.showTooltip();
    this.playModeEnabled$.next(true);
  }

  protected readonly ResourceItemModel = ResourceItemModel;
  protected readonly AppIconSource = AppIconSource;
  protected readonly LoadingLayoutType = LoadingLayoutType;
}
